import { FieldMetadata, PropertyType } from "types";
import {
  isValidEmail,
  isValidPhone,
  isValidStateOrStateCode,
  formatPhoneNumber,
  STATES_AND_PROVINCES,
} from "utils";

export type PropertyField =
  | "property.name"
  | "property.description"
  | "property.type"
  | "property.externalID"
  | "property.contact.name"
  | "property.contact.primaryEmail"
  | "property.contact.primaryPhone"
  | "property.location.street"
  | "property.location.city"
  | "property.location.stateCode"
  | "property.location.postalCode"
  | "property.data.Metadata.BillingCustomerKey"
  | "property.data.settings.notes";

export const propertyFieldMetadata: Record<
  PropertyField,
  FieldMetadata<PropertyField>
> = {
  "property.name": {
    field: "property.name",
    required: true,
    type: "string",
    category: "entity",
    validator: (value: string | undefined) => {
      if (!value) {
        return false;
      }
      return value.length > 0;
    },
    formatter: (value = "") => value.trim(),
    specialOptions: [
      {
        value: "Generate Data",
        label: "Generate Data",
      },
    ],
    alerts: [
      {
        severity: "info",
        title: "property name",
        disableClose: false,
        paragraphs: [
          "alerts.fields.name.info.p1",
          "alerts.fields.name.info.p2",
        ],
      },
      {
        severity: "error",
        title: "required",
        disableClose: false,
        paragraphs: [
          "alerts.fields.name.required.p1",
          "alerts.fields.name.required.p2",
        ],
      },
    ],
  },
  "property.description": {
    field: "property.description",
    required: false,
    type: "string",
    category: "entity",
    validator: () => true,
    formatter: (value = "") => value.trim(),
    specialOptions: [
      {
        value: "Leave Blank",
        label: "Leave Blank",
      },
    ],
    alerts: [
      {
        severity: "info",
        title: "property description",
        disableClose: false,
        paragraphs: [
          "alerts.fields.description.info.p1",
          "alerts.fields.description.info.p2",
        ],
      },
      {
        severity: "success",
        title: "optional",
        disableClose: false,
        paragraphs: ["alerts.fields.description.optional.p1"],
      },
    ],
  },
  "property.type": {
    field: "property.type",
    required: true,
    type: "enum",
    category: "entity",
    enumValues: [
      PropertyType.Residential,
      PropertyType.Commercial,
      PropertyType.Apartments,
    ],
    validator: (value: string | undefined) => {
      if (!value) {
        return false;
      }
      return [
        PropertyType.Residential,
        PropertyType.Commercial,
        PropertyType.Apartments,
      ].includes(value as PropertyType);
    },
    formatter: (value = "") => value.toLowerCase(),
    specialOptions: [
      {
        value: "Generate Data",
        label: "Generate Data",
      },
    ],
    alerts: [
      {
        severity: "info",
        title: "property type",
        disableClose: false,
        paragraphs: [
          "alerts.fields.type.info.p1",
          "alerts.fields.type.info.p2",
        ],
      },
      {
        severity: "error",
        title: "required",
        disableClose: false,
        paragraphs: [
          "alerts.fields.type.required.p1",
          "alerts.fields.type.required.p2",
        ],
      },
    ],
  },
  "property.externalID": {
    field: "property.externalID",
    required: false,
    type: "string",
    category: "entity",
    validator: () => true,
    formatter: (value = "") => value.trim(),
    specialOptions: [
      {
        value: "Leave Blank",
        label: "Leave Blank",
      },
    ],
    alerts: [
      {
        severity: "info",
        title: "external ID",
        disableClose: false,
        paragraphs: [
          "alerts.fields.externalID.info.p1",
          "alerts.fields.externalID.info.p2",
        ],
      },
      {
        severity: "success",
        title: "optional",
        disableClose: false,
        paragraphs: ["alerts.fields.externalID.optional.p1"],
      },
    ],
  },
  "property.data.Metadata.BillingCustomerKey": {
    field: "property.data.Metadata.BillingCustomerKey",
    required: false,
    type: "string",
    category: "entity",
    validator: () => true,
    formatter: (value = "") => value.trim(),
    specialOptions: [
      {
        value: "Leave Blank",
        label: "Leave Blank",
      },
    ],
    alerts: [
      {
        severity: "info",
        title: "billing customer",
        disableClose: false,
        paragraphs: [
          "alerts.fields.billingCustomer.info.p1",
          "alerts.fields.billingCustomer.info.p2",
        ],
      },
      {
        severity: "success",
        title: "optional",
        disableClose: false,
        paragraphs: ["alerts.fields.billingCustomer.optional.p1"],
      },
    ],
  },
  "property.data.settings.notes": {
    field: "property.data.settings.notes",
    required: false,
    type: "string",
    category: "entity",
    validator: () => true,
    formatter: (value = "") => value.trim(),
    specialOptions: [
      {
        value: "Leave Blank",
        label: "Leave Blank",
      },
    ],
    alerts: [
      {
        severity: "info",
        title: "notes",
        disableClose: false,
        paragraphs: [
          "alerts.fields.notes.info.p1",
          "alerts.fields.notes.info.p2",
        ],
      },
      {
        severity: "success",
        title: "optional",
        disableClose: false,
        paragraphs: ["alerts.fields.notes.optional.p1"],
      },
    ],
  },
  "property.contact.name": {
    field: "property.contact.name",
    required: true,
    type: "string",
    category: "contact",
    validator: (value: string | undefined) => {
      if (!value) {
        return false;
      }
      return value.length > 0;
    },
    formatter: (value = "") => value.trim(),
    specialOptions: [
      {
        value: "Generate Data",
        label: "Generate Data",
      },
    ],
    alerts: [
      {
        severity: "info",
        title: "contact name",
        disableClose: false,
        paragraphs: [
          "alerts.fields.contact_name.info.p1",
          "alerts.fields.contact_name.info.p2",
        ],
      },
      {
        severity: "error",
        title: "required",
        disableClose: false,
        paragraphs: [
          "alerts.fields.contact_name.required.p1",
          "alerts.fields.contact_name.required.p2",
        ],
      },
    ],
  },
  "property.contact.primaryEmail": {
    field: "property.contact.primaryEmail",
    required: false,
    type: "string",
    category: "contact",
    validator: (value: string | undefined) => {
      if (!value) {
        return true;
      }
      return isValidEmail(value);
    },
    formatter: (value = "") => value.toLowerCase().trim(),
    specialOptions: [
      {
        value: "Leave Blank",
        label: "Leave Blank",
      },
    ],
    alerts: [
      {
        severity: "info",
        title: "primary email",
        disableClose: false,
        paragraphs: ["alerts.fields.primaryEmail.info.p1"],
      },
      {
        severity: "success",
        title: "optional",
        disableClose: false,
        paragraphs: [
          "alerts.fields.primaryEmail.optional.p1",
          "alerts.fields.primaryEmail.optional.p2",
        ],
      },
    ],
  },
  "property.contact.primaryPhone": {
    field: "property.contact.primaryPhone",
    required: false,
    type: "string",
    category: "contact",
    validator: (value: string | undefined) => {
      if (!value) {
        return true;
      }
      return isValidPhone(value);
    },
    formatter: (value: string | undefined) => {
      if (!value) {
        return "";
      }
      return formatPhoneNumber(value.replace(/\D/g, ""));
    },
    specialOptions: [
      {
        value: "Leave Blank",
        label: "Leave Blank",
      },
    ],
    alerts: [
      {
        severity: "info",
        title: "primary phone",
        disableClose: false,
        paragraphs: ["alerts.fields.primaryPhone.info.p1"],
      },
      {
        severity: "success",
        title: "optional",
        disableClose: false,
        paragraphs: [
          "alerts.fields.primaryPhone.optional.p1",
          "alerts.fields.primaryPhone.optional.p2",
        ],
      },
    ],
  },
  "property.location.street": {
    field: "property.location.street",
    required: true,
    type: "string",
    category: "location",
    validator: (value: string | undefined) => {
      if (!value) {
        return false;
      }
      return value.length > 0;
    },
    formatter: (value = "") => value.trim(),
    specialOptions: [],
    alerts: [
      {
        severity: "info",
        title: "street",
        disableClose: false,
        paragraphs: ["alerts.fields.street.info.p1"],
      },
      {
        severity: "error",
        title: "required",
        disableClose: false,
        paragraphs: [
          "alerts.fields.street.required.p1",
          "alerts.fields.street.required.p2",
        ],
      },
    ],
  },
  "property.location.city": {
    field: "property.location.city",
    required: false,
    type: "string",
    category: "location",
    validator: () => true,
    formatter: (value = "") => value.trim(),
    specialOptions: [
      {
        value: "Leave Blank",
        label: "Leave Blank",
      },
    ],
    alerts: [
      {
        severity: "info",
        title: "city",
        disableClose: false,
        paragraphs: ["alerts.fields.city.info.p1"],
      },
      {
        severity: "error",
        title: "required",
        disableClose: false,
        paragraphs: [
          "alerts.fields.city.required.p1",
          "alerts.fields.city.required.p2",
        ],
      },
    ],
  },
  "property.location.stateCode": {
    field: "property.location.stateCode",
    required: false,
    type: "enum",
    category: "location",
    enumValues: STATES_AND_PROVINCES.map((state) => state.stateCode),
    validator: (value: string | undefined) => {
      if (!value) {
        return false;
      }
      return isValidStateOrStateCode(value);
    },
    formatter: (value: string) => {
      if (value?.length > 3) {
        const state = STATES_AND_PROVINCES.find(
          (state) => state.label.toLowerCase() === value.toLowerCase(),
        );
        return state ? state.stateCode : "Invalid State";
      } else {
        const stateCode = STATES_AND_PROVINCES.find(
          (state) => state.stateCode === value,
        );
        return stateCode ? stateCode.label : "Invalid State";
      }
    },
    specialOptions: [
      {
        value: "Leave Blank",
        label: "Leave Blank",
      },
      {
        value: "Generate Data",
        label: "Generate Data",
      },
    ],
    alerts: [
      {
        severity: "info",
        title: "state",
        disableClose: false,
        paragraphs: ["alerts.fields.stateCode.info.p1"],
      },
      {
        severity: "error",
        title: "required",
        disableClose: false,
        paragraphs: [
          "alerts.fields.stateCode.required.p1",
          "alerts.fields.stateCode.required.p2",
        ],
      },
    ],
  },
  "property.location.postalCode": {
    field: "property.location.postalCode",
    required: false,
    type: "string",
    category: "location",
    validator: (value: string | undefined) => {
      if (!value) {
        return true;
      }
      return value.length >= 5;
    },
    formatter: (value = "") => value.trim(),
    specialOptions: [
      {
        value: "Leave Blank",
        label: "Leave Blank",
      },
    ],
    alerts: [
      {
        severity: "info",
        title: "postal code",
        disableClose: false,
        paragraphs: ["alerts.fields.postalCode.info.p1"],
      },
      {
        severity: "error",
        title: "required",
        disableClose: false,
        paragraphs: [
          "alerts.fields.postalCode.required.p1",
          "alerts.fields.postalCode.required.p2",
        ],
      },
    ],
  },
};
